import { useCallback, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { SlideThemeCatalogRead } from '../../types/api';
import { updateBrandIdentity } from './knowledgeBaseApi';
import { useBrandIdentity } from './useBrandIdentity';

export interface BrandDefaultThemeResult {
  /** The look new rendered posts start in. Null until the brand picks one. */
  defaultThemeId: string | null;
  isLoading: boolean;
  /** Load or save failure — whichever is current. */
  error: string | null;
  /** Inline confirmation of the last save. Clears when the next one starts. */
  notice: string | null;
  isSaving: boolean;
  setDefault: (theme: SlideThemeCatalogRead) => void;
}

/**
 * The brand's default look, and the one write that changes it.
 *
 * The tick moves before the PUT lands and moves back if it fails: this is a
 * one-field preference on a record the screen already holds, so waiting on a
 * round trip to acknowledge a click would be the only slow thing on the page.
 * Nothing else depends on the value in flight — a run reads the default
 * server-side when it is queued, not from here.
 *
 * The brand-identity PUT is a partial upsert, so `{ default_theme_id }` travels
 * alone: the gallery must never send a tone, a palette or a never-say list it
 * did not collect.
 */
export function useBrandDefaultTheme(businessId: string | null): BrandDefaultThemeResult {
  const { identity, isLoading, error: loadError } = useBrandIdentity(businessId);
  // Null means "no local opinion yet"; `{ id: null }` is a real cleared value.
  const [override, setOverride] = useState<{ id: string | null } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const defaultThemeId = override !== null ? override.id : (identity?.default_theme_id ?? null);

  const setDefault = useCallback(
    (theme: SlideThemeCatalogRead) => {
      if (!businessId) return;

      const previous = override !== null ? override.id : (identity?.default_theme_id ?? null);
      if (previous === theme.id) return;

      setOverride({ id: theme.id });
      setSaveError(null);
      setNotice(`${theme.name} is now the default visual style for new rendered posts.`);
      setIsSaving(true);

      void (async () => {
        try {
          await updateBrandIdentity(businessId, { default_theme_id: theme.id });
        } catch (caught) {
          setOverride({ id: previous });
          setNotice(null);
          setSaveError(getErrorMessage(caught, 'That default did not save. Try again.'));
        } finally {
          setIsSaving(false);
        }
      })();
    },
    [businessId, identity, override],
  );

  return {
    defaultThemeId,
    isLoading,
    error: saveError ?? loadError,
    notice,
    isSaving,
    setDefault,
  };
}
